// The live editable canvas that takes the place of the static media <img> inside a
// platform preview. Renders the Composition, a direct-manipulation selection overlay,
// the floating contextual toolbar, and (desktop) the glossy editing bar pinned to the top of
// the visual — all driven by the shared editor context. Returns null when there's no editable
// composition, so the preview falls back to its normal media.

import { useRef, useState, type ReactNode } from 'react'
import { CompositionCanvas } from '../CompositionCanvas'
import { SelectionOverlay } from './SelectionOverlay'
import { FloatingToolbar } from './FloatingToolbar'
import { useComposerEditor, useReelPlayhead, useVideoTimeline } from './CompositionEditorProvider'
import { playheadTrueView } from './playhead-view'
import { useVisualHover } from './use-visual-hover'
import { useLayerOverflowStore } from './use-layer-overflow'
import { useSectionContextStore } from '../../store/conversation-store'

export function ComposerCanvasMedia({
  interactive = true,
  onTap,
}: {
  /** When false, render the composition READ-ONLY (no selection/toolbar) — a live, accurate
   *  preview that reflects edits but isn't manipulable. Used by the mobile preview-first home,
   *  where tapping drills into the dedicated image editor instead of editing in place. */
  interactive?: boolean
  /** Tap handler for the read-only display (drills into the image editor). */
  onTap?: () => void
} = {}) {
  const ctx = useComposerEditor()
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  // Selectors return stable fns, so this component does NOT re-render when `active` toggles.
  const showBar = useVisualHover(s => s.show)
  const hideBar = useVisualHover(s => s.hide)
  const setChatContext = useSectionContextStore(s => s.setContext)
  const setOverflowing = useLayerOverflowStore(s => s.setOverflowing)
  // A VIDEO beat's overlay is a transparent canvas BY DESIGN — it composites over the beat's
  // motion background in the stage. Without this the no-background fallback paints opaque
  // #0B0B0C and hides the clip behind the text (the "black preview" bug).
  const timeline = useVideoTimeline()
  const isVideoOverlay = timeline != null
  // Subscribed above every early return (rules-of-hooks); ticks only ever fire on video.
  const playhead = useReelPlayhead(timeline)
  if (!ctx) return null
  const { editor, dock } = ctx
  const dims = { w: editor.comp.canvas.width, h: editor.comp.canvas.height }

  // PLAYHEAD-TRUE paused canvas (video only) — see playhead-view.ts. Shared with the mobile
  // stage so the window-gating math can never drift between surfaces.
  const view = timeline
    ? playheadTrueView(editor, timeline, playhead)
    : { displayComp: editor.comp }
  const { displayComp, beatTime, hitDisabledIds } = view

  // Read-only display: the live composition, tappable to drill into the editor. No overlay.
  if (!interactive) {
    return (
      <div
        data-canvas-visual
        role={onTap ? 'button' : undefined}
        aria-label={onTap ? 'Edit image' : undefined}
        onClick={onTap}
        className={`relative w-full select-none ${onTap ? 'cursor-pointer' : ''}`}
      >
        <CompositionCanvas
          composition={displayComp}
          beatTime={beatTime}
          transparent={isVideoOverlay}
        />
      </div>
    )
  }

  // "Aa" / double-click on a text layer → inline typing on the canvas (deeper style controls
  // still live in the dock). Non-text layers fall back to the dock.
  const editText = () => {
    if (editor.selected?.type === 'text') editor.beginTextEdit(editor.selected.id)
    else dock.openDock()
  }

  // Ask Taka about the SELECTED LAYER: pin a layer-scoped context chip on the chat, so a
  // revise knows exactly which element the user means.
  const askTakaForLayer = () => {
    const layer = editor.selected
    if (!layer) return
    const text =
      layer.type === 'text'
        ? layer.runs
            .map(r => r.text)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim()
        : ''
    setChatContext(ctx.creativeId, {
      sectionId: 'image',
      sectionType: 'image',
      label:
        layer.type === 'text' ? `Text “${text.slice(0, 40) || 'layer'}”` : `${layer.type} layer`,
      preview: text || 'Selected layer on the visual',
    })
  }

  return (
    <div
      ref={ref}
      data-canvas-visual
      className="relative w-full select-none"
      onMouseEnter={showBar}
      onMouseLeave={hideBar}
    >
      <CompositionCanvas
        composition={displayComp}
        beatTime={beatTime}
        transparent={isVideoOverlay}
        onScaleChange={i => setScale(i.scale)}
        suppressLayerId={editor.editingId ?? undefined}
        onTextOverflow={setOverflowing}
      >
        <SelectionOverlay
          editor={editor}
          scale={scale}
          containerRef={ref}
          hitDisabledIds={hitDisabledIds}
        />
        <FloatingToolbar
          editor={editor}
          scale={scale}
          dims={dims}
          onMore={dock.openDock}
          onEditText={editText}
          onAskTaka={askTakaForLayer}
          containerRef={ref}
        />
      </CompositionCanvas>
    </div>
  )
}

/** The live canvas. The global editing chrome (undo/redo · versions · add · layers) now lives in
 *  the composer-level control bar at the top of the composer (ComposerEditorBar), NOT on the
 *  image — the photo is just one layer. Per-layer tools stay on the canvas (FloatingToolbar).
 *  `clipClassName` is the (overflow-hidden / rounded) wrapper that clips the image. */
export function CanvasMediaWithBar({
  clipClassName,
  interactive = true,
  onTap,
}: {
  clipClassName?: string
  interactive?: boolean
  onTap?: () => void
}) {
  return (
    <div className="relative w-full">
      <div className={clipClassName}>
        <ComposerCanvasMedia interactive={interactive} onTap={onTap} />
      </div>
    </div>
  )
}

/** True when the composer should show the live canvas for this creative. */
export function useHasComposerCanvas(): boolean {
  return useComposerEditor() != null
}

/** Wraps the canvas viewport so a click on empty space (anywhere outside a layer, handle, or
 *  toolbar) deselects the current layer — "tap outside to deselect". Layer/handle/toolbar
 *  pointer-downs stopPropagation, so only background clicks reach here. */
export function CanvasDeselectArea({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  const ctx = useComposerEditor()
  return (
    <div className={className} onPointerDown={ctx ? () => ctx.editor.select(null) : undefined}>
      {children}
    </div>
  )
}
